
import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Container, Modal, ModalHeader, ModalBody, ModalFooter, Label, Input, FormGroup, Row } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiGet, apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3, H4, H5, ProgressBar } from "../../AbstractElements";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Trash2, Repeat, Eye, Truck, FileText, User, Image as ImageIcon } from 'react-feather';
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { mediaUrl } from "../../Utils";
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/AuthContext";
import { useConfig } from "../../Context/ConfigContext";

interface VehicleData {
    id: number;
    branch_id: number;
    status: string;
    category: string;
    brand: string;
    model: string;
    manufacture_year: number;
    color: string;
    registration_number: string;
    registration_expiry_date: string; // YYYY-MM-DD
    permit_expiry_date?: string; // Added field
    rto_authority?: string;
    permit_authority?: string;
    permit_number?: string;
    vin_chassis_number: string;
    assigned_driver_id?: number;
    purchase_date: string;
    purchase_cost: number;
    vehicle_image?: string;
    rc_document?: string;
    next_service_due: string;
    owner_name?: string;
    owner_mobile?: string;
    owner_email?: string;
    fuel_type?: string;
    insurance_provider?: string;
    insurance_policy_number?: string;
    insurance_expiry_date?: string;
    warranty_end_date?: string;
    warranty_km?: string;
    branch?: { branch_name: string; deleted_at?: string };
    driver?: { name: string; deleted_at?: string };
}

export default function ViewVehicle() {
    const [data, setData] = useState<VehicleData[]>([]);
    const { config } = useConfig();
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("");
    const prevSearch = React.useRef(searchTerm);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    // Transfer Modal State
    const [branches, setBranches] = useState<any[]>([]);
    const [drivers, setDrivers] = useState<any[]>([]);
    const [transferModal, setTransferModal] = useState(false);
    const [transferData, setTransferData] = useState({
        vehicleId: 0,
        vehicleReg: '',
        currentBranch: '',
        currentDriver: '',
        newBranchId: '',
        newDriverId: '',
        driver_assigned_at: '',
        odometer_reading: ''
    });
    const [transferLoading, setTransferLoading] = useState(false);

    // View Modal State
    const [viewModal, setViewModal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const [selectedVehicle, setSelectedVehicle] = useState<VehicleData | null>(null);
    const [activeTab, setActiveTab] = useState('1');

    const toggleViewModal = () => {
        if (viewModal) {
            handleClose();
        } else {
            setViewModal(true);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setViewModal(false);
            setIsClosing(false);
        }, 550);
    };

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    const handleView = (vehicle: VehicleData) => {
        setSelectedVehicle(vehicle);
        setActiveTab('1');
        setViewModal(true);
    };

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const branchData = await apiPost("branches/list", { status: 'Active' });
                setBranches(branchData);
            } catch (error) {
                console.error("Error fetching options:", error);
            }
        };
        fetchOptions();
    }, []);

    const fetchDriversForBranch = async (branchId: string) => {
        try {
            const response: any = await apiPost("drivers/list", { branch_id: branchId });
            // If the API returns paginated data (wrapper), handle it.
            // Our get_all returns JSON array if no per_page, or object if per_page.
            // We are not passing per_page, so it returns all (array).
            if (Array.isArray(response)) {
                setDrivers(response);
            } else if (response.data) {
                setDrivers(response.data);
            } else {
                setDrivers([]);
            }
        } catch (error) {
            console.error("Error fetching drivers:", error);
            setDrivers([]);
        }
    };

    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        prevSearch.current = searchTerm;

        // Reset to page 1 when search changes
        if (isSearchChanged && currentPage !== 1) {
            setCurrentPage(1);
            return; // Exit early, let page change trigger next fetch
        }

        const delay = isSearchChanged ? 500 : 0;
        const timer = setTimeout(() => {
            fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
        }, delay);
        return () => clearTimeout(timer);
    }, [refresh, currentPage, perPage, searchTerm, sortColumn, sortDirection]);

    const fetchData = async (page: number, size: number, search: string = "", sortCol: string = "", sortDir: string = "") => {
        setLoading(true);
        try {
            const body: any = {
                page: page,
                per_page: size,
                search: search
            };

            if (sortCol) {
                body.sort_by = sortCol;
                body.sort_direction = sortDir;
            }

            const response: any = await apiPost("vehicles/getall", body);
            if (response && response.data) {
                setData(response.data);
                setTotalRows(response.total || 0);
            } else {
                setData(response);
                setTotalRows(response.length || 0);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: any, sortDir: string) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(sortDir);
            setCurrentPage(1);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleAdd = () => {
        navigate(`${process.env.PUBLIC_URL}/add_vehicle`);
    };

    const handleEdit = (id: number) => {
        navigate(`${process.env.PUBLIC_URL}/edit_vehicle`, { state: { id: id } });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("vehicles/delete", { id: id });
                    toast.success("Vehicle deleted successfully", { autoClose: 1000 });
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete vehicle", { autoClose: 1000 });
                }
            }
        });
    };

    const openTransferModal = (row: VehicleData) => {
        setTransferData({
            vehicleId: row.id,
            vehicleReg: row.registration_number,
            currentBranch: row.branch?.branch_name || 'N/A',
            currentDriver: row.driver?.name || 'Unassigned',
            newBranchId: '',
            newDriverId: '',
            driver_assigned_at: new Date().toISOString(),
            odometer_reading: ''
        });
        setDrivers([]);
        setTransferModal(true);
    };

    const handleTransferSubmit = async () => {
        if (!transferData.newBranchId) {
            toast.error("Please select a branch");
            return;
        }
        if (!transferData.newDriverId) {
            toast.error("Please select a driver");
            return;
        }

        setTransferLoading(true);
        try {
            await apiPost("vehicles/transfer", {
                vehicle_id: transferData.vehicleId,
                branch_id: transferData.newBranchId,
                driver_id: transferData.newDriverId,
                odometer_reading: transferData.odometer_reading,
                driver_assigned_at: transferData.driver_assigned_at
            });
            toast.success("Vehicle transferred successfully");
            setTransferModal(false);
            setRefresh(prev => prev + 1);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Transfer failed");
        } finally {
            setTransferLoading(false);
        }
    };



    const calculateProfileCompletion = (row: VehicleData) => {
        const mandatoryFields = [
            'branch_id', 'status', 'category', 'brand', 'model',
            'manufacture_year', 'color', 'registration_number', 'registration_expiry_date',
            'vin_chassis_number', 'assigned_driver_id', 'purchase_date', 'purchase_cost',
            'next_service_due', 'insurance_provider', 'insurance_policy_number', 'insurance_expiry_date',
            'owner_name', 'owner_mobile', 'owner_email', 'fuel_type',
            'vehicle_image', 'rc_document'
        ];
        let filledCount = 0;
        mandatoryFields.forEach(field => {
            const value = row[field as keyof VehicleData];
            if (value !== null && value !== undefined && value !== "") {
                filledCount++;
            }
        });
        return Math.min(Math.round((filledCount / mandatoryFields.length) * 100), 100);
    };

    const filteredDrivers = drivers.filter(d => d.assigned_branch_id?.toString() === transferData.newBranchId?.toString());

    const columns = [
        {
            name: <div>Sl. No</div>,
            selector: (row: VehicleData, index?: number) => (currentPage - 1) * perPage + (index || 0) + 1,
            width: "80px",
        },
        {
            name: <div>Model</div>,
            selector: (row: VehicleData) => `${row.brand} ${row.model}`,
            sortable: true,
            sortField: "model",
            wrap: true,
        },
        {
            name: <div>Reg No</div>,
            selector: (row: VehicleData) => row.registration_number,
            sortable: true,
            sortField: "registration_number",
            wrap: true,
        },

        {
            name: <div>Driver</div>,
            cell: (row: VehicleData) => (
                <div>
                    {row.driver?.name || "Unassigned"}
                    {row.driver?.deleted_at && <span className="text-danger small ms-1">(Deleted)</span>}
                </div>
            ),
            sortable: true,
            sortField: "driver_name",
            wrap: true,
        },
        {
            name: <div>Branch</div>,
            cell: (row: VehicleData) => (
                <div>
                    {row.branch?.branch_name || "Unassigned"}
                    {row.branch?.deleted_at && <span className="text-danger small ms-1">(Deleted)</span>}
                </div>
            ),
            sortable: true,
            sortField: "branch_name",
            wrap: true,
        },
        {
            name: <div>Next Service</div>,
            selector: (row: VehicleData) => row.next_service_due,
            sortable: true,
            sortField: "next_service_due",
            cell: (row: VehicleData) => row.next_service_due ? new Date(row.next_service_due).toLocaleDateString('en-GB') : "N/A"
        },
        {
            name: <div>Profile Status</div>,
            cell: (row: VehicleData) => {
                const completion = calculateProfileCompletion(row);
                return (
                    <div className="w-100">
                        <div className="progress-bar-animated progress-bar-striped">
                            <ProgressBar
                                value={completion}
                                color={completion === 100 ? 'success' : 'warning'}
                                striped={completion <= 100}
                                animated={completion < 100}
                                style={{ height: '8px' }}
                            />
                        </div>
                        <div className="text-center small mt-1">{completion}%</div>
                    </div>
                );
            },
            sortable: false,
            width: "150px"
        },
        {
            name: <div>Status</div>,
            cell: (row: VehicleData) => (
                <span className={`badge ${row.status === 'Active' ? 'bg-success' : row.status === 'Maintenance' ? 'bg-warning' : 'bg-secondary'}`}>
                    {row.status}
                </span>
            ),
            sortable: true,
            sortField: "status",
        },
        {
            name: <div>Year</div>,
            selector: (row: VehicleData) => row.manufacture_year,
            sortable: true,
            omit: true
        },
        {
            name: <div>Color</div>,
            selector: (row: VehicleData) => row.color,
            sortable: true,
            omit: true
        },
        {
            name: <div>Fuel</div>,
            selector: (row: VehicleData) => row.fuel_type || '',
            sortable: true,
            omit: true
        },
        {
            name: <div>Reg Expiry</div>,
            selector: (row: VehicleData) => row.registration_expiry_date,
            sortable: true,
            omit: true
        },
        {
            name: <div>VIN</div>,
            selector: (row: VehicleData) => row.vin_chassis_number,
            sortable: true,
            omit: true
        },
        {
            name: <div>Purchase Date</div>,
            selector: (row: VehicleData) => row.purchase_date,
            sortable: true,
            omit: true
        },
        {
            name: <div>Cost ({config.currency})</div>,
            selector: (row: VehicleData) => row.purchase_cost,
            sortable: true,
            omit: true
        },

        {
            name: <div>Insurance</div>,
            selector: (row: VehicleData) => row.insurance_provider || '',
            sortable: true,
            omit: true
        },
        {
            name: <div>Policy No</div>,
            selector: (row: VehicleData) => row.insurance_policy_number || '',
            sortable: true,
            omit: true
        },
        {
            name: "Ins Expiry",
            selector: (row: VehicleData) => row.insurance_expiry_date || '',
            sortable: true,
            omit: true
        },
        {
            name: "Owner",
            selector: (row: VehicleData) => row.owner_name || '',
            sortable: true,
            omit: true
        },
        {
            name: <div>Actions</div>,
            cell: (row: VehicleData) => (
                <div className="d-flex justify-content-between gap-2">
                    {hasPermission('vehicle.view') && (

                        <Button size="sm" color="primary" onClick={() => handleView(row)} title="View Details">
                            <Eye size={14} />
                        </Button>
                    )}
                    {hasPermission('vehicle.edit') && (

                        <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                            <Edit size={14} />
                        </Button>
                    )}
                    {hasPermission('vehicle.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                    {hasPermission('vehicle.transfer') && row.branch_id && row.assigned_driver_id && row.status !== 'Sold' && row.status !== 'Destroyed' && (
                        <Button size="sm" color="warning" onClick={() => openTransferModal(row)} title="Transfer Vehicle">
                            <Repeat size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="View Vehicle" parent="General" title="View Vehicle" />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                <H3 className="text-primary">Vehicle Overview</H3>
                                {hasPermission('vehicle.add') && (
                                    <Btn color="primary" onClick={handleAdd}>
                                        <Plus size={16} /> Add Vehicle
                                    </Btn>
                                )}
                            </Col>
                            <>
                                <Col xs={12} className="mb-3 d-flex justify-content-end">
                                    <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                                </Col>

                                <div className="table-responsive">
                                    <DataTable
                                        data={data}
                                        columns={columns}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        onChangePage={handlePageChange}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        onSort={handleSort}
                                        sortServer
                                        progressPending={loading}
                                        progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                        highlightOnHover
                                        customStyles={customStyles}
                                    />
                                </div>
                            </>

                        </CardBody>
                    </Card>
                </Col>
            </Container>



            {/* View Vehicle Modal */}
            <Modal isOpen={viewModal} toggle={toggleViewModal} size="lg" centered contentClassName={isClosing ? "animate-3d-slit-out" : "animate-3d-slit"} fade={false}>
                <div style={{
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    padding: '1.5rem',
                    position: 'relative', width: '100%'
                }}>
                    <button
                        className="btn-close btn-close-white"
                        type="button"
                        onClick={handleClose}
                        style={{ position: 'absolute', top: '1rem', right: '1rem', opacity: 0.9 }}
                    ></button>

                    {selectedVehicle && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                            <div style={{
                                width: '100px',
                                height: '100px',
                                borderRadius: '10px',
                                overflow: 'hidden',
                                border: '3px solid rgba(255,255,255,0.3)',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                                flexShrink: 0,
                                backgroundColor: '#f8f9fa',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                <img
                                    src={(selectedVehicle as any).vehicle_image ? `${mediaUrl}${(selectedVehicle as any).vehicle_image}` : 'https://via.placeholder.com/100/1e3c72/ffffff?text=Vehicle'}
                                    alt="Vehicle"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/100/1e3c72/ffffff?text=Vehicle';
                                    }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '1.75rem',
                                    margin: 0,
                                    marginBottom: '0.5rem'
                                }}>
                                    {selectedVehicle.brand} {selectedVehicle.model}
                                </h3>
                                <div style={{ display: 'flex', gap: '1rem', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>
                                    <span style={{ fontWeight: 600 }}>{selectedVehicle.registration_number}</span>
                                    <span>•</span>
                                    <span>{selectedVehicle.category}</span>
                                </div>
                            </div>
                            <div>
                                <span style={{
                                    backgroundColor: selectedVehicle.status === 'Active' ? 'rgba(16, 185, 129, 0.2)' : selectedVehicle.status === 'Maintenance' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(255,255,255,0.2)',
                                    color: selectedVehicle.status === 'Active' ? '#10b981' : selectedVehicle.status === 'Maintenance' ? '#f59e0b' : 'white',
                                    padding: '0.6rem 1.2rem',
                                    borderRadius: '25px',
                                    fontSize: '0.875rem',
                                    fontWeight: 700,
                                    border: `2px solid ${selectedVehicle.status === 'Active' ? '#10b981' : selectedVehicle.status === 'Maintenance' ? '#f59e0b' : 'rgba(255,255,255,0.3)'}`,
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    {selectedVehicle.status}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-body p-0">
                    <Nav tabs className="nav-primary" style={{ padding: '0 1.5rem', marginTop: '1rem', borderBottom: '1px solid #dee2e6' }}>
                        <NavItem>
                            <NavLink
                                className={activeTab === '1' ? 'active' : ''}
                                onClick={() => toggleTab('1')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <Truck size={16} /> Overview
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={activeTab === '2' ? 'active' : ''}
                                onClick={() => toggleTab('2')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <FileText size={16} /> Documents & Dates
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={activeTab === '3' ? 'active' : ''}
                                onClick={() => toggleTab('3')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <User size={16} /> Owner & Financial
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={activeTab === '4' ? 'active' : ''}
                                onClick={() => toggleTab('4')}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px' }}
                            >
                                <ImageIcon size={16} /> Images
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <div className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '300px' }}>
                        {selectedVehicle && (
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Branch</label>
                                                    <div className="fw-semibold text-primary">
                                                        {selectedVehicle.branch?.branch_name || 'N/A'}
                                                        {selectedVehicle.branch?.deleted_at && <span className="text-danger small ms-1">(Deleted)</span>}
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Assigned Driver</label>
                                                    <div className="fw-semibold">
                                                        {selectedVehicle.driver?.name || 'Unassigned'}
                                                        {selectedVehicle.driver?.deleted_at && <span className="text-danger small ms-1">(Deleted)</span>}
                                                    </div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Manufacture Year</label>
                                                    <div className="fw-semibold">{selectedVehicle.manufacture_year || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Vehicle Type</label>
                                                    <div className="fw-semibold">{selectedVehicle.category || 'N/A'}</div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Color</label>
                                                    <div className="fw-semibold">{selectedVehicle.color || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Fuel Type</label>
                                                    <div className="fw-semibold">{selectedVehicle.fuel_type || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">VIN / Chassis</label>
                                                    <div className="fw-semibold text-monospace">{selectedVehicle.vin_chassis_number || 'N/A'}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="2">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <h6 className="fw-bold mb-3 text-primary">Registration & Permits</h6>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Registration Expiry</label>
                                                    <div className="fw-semibold">{selectedVehicle.registration_expiry_date || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">RTO Authority</label>
                                                    <div className="fw-semibold">{selectedVehicle.rto_authority || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Permit Expiry</label>
                                                    <div className="fw-semibold">{selectedVehicle.permit_expiry_date || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Permit Policy Number</label>
                                                    <div className="fw-semibold">{selectedVehicle.permit_number || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Permit Authority</label>
                                                    <div className="fw-semibold">{selectedVehicle.permit_authority || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Next Service Due</label>
                                                    <div className="fw-semibold ">{selectedVehicle.next_service_due || 'N/A'}</div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <h6 className="fw-bold mb-3 text-primary">Insurance Details</h6>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Provider</label>
                                                    <div className="fw-semibold">{selectedVehicle.insurance_provider || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Policy Number</label>
                                                    <div className="fw-semibold">{selectedVehicle.insurance_policy_number || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Insurance Expiry</label>
                                                    <div className="fw-semibold">{selectedVehicle.insurance_expiry_date || 'N/A'}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="3">
                                    <div className="bg-white p-4 rounded shadow-sm">
                                        <div className="row g-4">
                                            <div className="col-md-6">
                                                <h6 className="fw-bold mb-3 text-primary">Owner Details</h6>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Owner Name</label>
                                                    <div className="fw-semibold">{selectedVehicle.owner_name || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Owner Contact</label>
                                                    <div className="fw-semibold">{selectedVehicle.owner_mobile || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Owner Email</label>
                                                    <div className="fw-semibold">{selectedVehicle.owner_email || 'N/A'}</div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <h6 className="fw-bold mb-3 text-primary">Financial Info</h6>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Purchase Date</label>
                                                    <div className="fw-semibold">{selectedVehicle.purchase_date || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Purchase Cost ({config.currency})</label>
                                                    <div className="fw-semibold">{selectedVehicle.purchase_cost ? `${Number(selectedVehicle.purchase_cost).toLocaleString()} ${config.currency}` : 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Warranty End Date</label>
                                                    <div className="fw-semibold">{selectedVehicle.warranty_end_date || 'N/A'}</div>
                                                </div>
                                                <div className="mb-3">
                                                    <label className="text-muted small fw-bold text-uppercase">Warranty KM</label>
                                                    <div className="fw-semibold">{selectedVehicle.warranty_km || 'N/A'}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>

                                <TabPane tabId="4">
                                    <div className="row g-4">
                                        <div className="col-md-6">
                                            <div className="bg-white p-3 rounded shadow-sm h-100">
                                                <h6 className="fw-bold mb-3 border-bottom pb-2">Vehicle Image</h6>
                                                <div className="text-center bg-light rounded p-2" style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {selectedVehicle.vehicle_image ? (
                                                        <img
                                                            src={`${mediaUrl}${selectedVehicle.vehicle_image}`}
                                                            alt="Vehicle"
                                                            className="img-fluid rounded"
                                                            style={{ maxHeight: '250px', objectFit: 'contain' }}
                                                        />
                                                    ) : <span className="text-muted">No Image Available</span>}
                                                </div>
                                                {selectedVehicle.vehicle_image && (
                                                    <div className="mt-2 text-center">
                                                        <a href={`${mediaUrl}${selectedVehicle.vehicle_image}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                                                            View Full Size
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="bg-white p-3 rounded shadow-sm h-100">
                                                <h6 className="fw-bold mb-3 border-bottom pb-2">RC Document</h6>
                                                <div className="text-center bg-light rounded p-2 " style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {selectedVehicle.rc_document ? (
                                                        <img
                                                            src={`${mediaUrl}${selectedVehicle.rc_document}`}
                                                            alt="RC Document"
                                                            className="img-fluid rounded"
                                                            style={{ maxHeight: '250px', objectFit: 'contain' }}
                                                        />
                                                    ) : <span className="text-muted">No Document Available</span>}
                                                </div>
                                                {selectedVehicle.rc_document && (
                                                    <div className="mt-2 text-center">
                                                        <a href={`${mediaUrl}${selectedVehicle.rc_document}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                                                            View Full Size
                                                        </a>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </TabPane>
                            </TabContent>
                        )}
                    </div>
                </div>
            </Modal>

            <Modal isOpen={transferModal} toggle={() => setTransferModal(!transferModal)}>
                <ModalHeader toggle={() => setTransferModal(!transferModal)}>Transfer Vehicle</ModalHeader>
                <ModalBody>
                    <Row>
                        <Col md={6}>
                            <Label>Current Branch</Label>
                            <Input value={transferData.currentBranch} disabled />
                        </Col>
                        <Col md={6}>
                            <Label>Current Driver</Label>
                            <Input value={transferData.currentDriver} disabled />
                        </Col>
                    </Row>
                    <hr />
                    <Row className="mt-3">
                        <Col md={12}>
                            <FormGroup>
                                <Label>Transfer To Branch <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    value={transferData.newBranchId}
                                    onChange={(e) => {
                                        const newBranchId = e.target.value;
                                        setTransferData({ ...transferData, newBranchId: newBranchId, newDriverId: '' });
                                        if (newBranchId) {
                                            fetchDriversForBranch(newBranchId);
                                        } else {
                                            setDrivers([]);
                                        }
                                    }}
                                    autoComplete="none"
                                >
                                    <option value="">Select Branch</option>
                                    {branches.map((b: any) => (
                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={12}>
                            <FormGroup>
                                <Label>Assign Driver <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    value={transferData.newDriverId}
                                    onChange={(e) => setTransferData({ ...transferData, newDriverId: e.target.value })}
                                    autoComplete="none"
                                >
                                    <option value="">Select Driver</option>
                                    {drivers.map((d: any) => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </Input>
                            </FormGroup>

                        </Col>
                        {transferData.newBranchId && transferData.newDriverId && (
                            <>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Transfer Date & Time <span className="text-danger">*</span></Label>
                                        <DatePicker
                                            className="form-control"
                                            selected={transferData.driver_assigned_at ? new Date(transferData.driver_assigned_at) : null}
                                            onChange={(date) => setTransferData({ ...transferData, driver_assigned_at: date ? date.toISOString() : '' })}
                                            showTimeSelect
                                            dateFormat="dd/MM/yyyy HH:mm"
                                            placeholderText="Select Date & Time"
                                            autoComplete="off"
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Odometer Reading <span className="text-danger">*</span></Label>
                                        <Input
                                            type="number"
                                            placeholder="Enter Odometer Reading"
                                            value={transferData.odometer_reading}
                                            onChange={(e) => setTransferData({ ...transferData, odometer_reading: e.target.value })}
                                        />
                                    </FormGroup>
                                </Col>
                            </>
                        )}
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setTransferModal(false)}>Cancel</Button>
                    <Button color="primary" disabled={transferLoading} onClick={handleTransferSubmit}>
                        {transferLoading ? <> Transferring <Spinner size="sm" /></> : "Transfer"}
                    </Button>
                </ModalFooter>
            </Modal>
            <ToastContainer autoClose={1000} />
        </div >
    );
}
